import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Paperclip, Phone } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/kit";
import { toast } from "sonner";

const KIND_TONE = { feed: "pos", query: "primary", command: "warn", agenda: "primary", execution: "pos", rejected: "neg", denied: "neg", error: "neg", pending: "warn", clarify: "primary", document: "pos", push: "primary" };

export default function Simulator() {
  const { user } = useAuth();
  const [emps, setEmps] = useState([]);
  const [phone, setPhone] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/employees").then(({ data }) => {
      const withPhone = data.filter((e) => e.phone);
      setEmps(withPhone);
      const mine = withPhone.find((e) => e.id === user?.id);
      setPhone((mine || withPhone[0])?.phone || "");
    }).catch(() => {});
  }, [user]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const push = (m) => setMsgs((p) => [...p, m]);

  useEffect(() => {
    if (!phone) return;
    let seen = new Set();
    const poll = () => api.get(`/messages/inbox?phone=${phone}`).then(({ data }) => {
      const fresh = data.filter((m) => !seen.has(m.id) && (m.status === "sent" || m.status === "delivered"));
      if (fresh.length) {
        fresh.forEach((m) => seen.add(m.id));
        setMsgs((p) => [...p, ...fresh.map((m) => ({ who: "isa", text: m.text, kind: "push",
          document_id: m.document_id, file_url: m.file_url, media_type: m.media_type }))]);
      }
    }).catch(() => {});
    api.get(`/messages/inbox?phone=${phone}`).then(({ data }) => { data.forEach((m) => seen.add(m.id)); }).catch(() => {});
    const i = setInterval(poll, 5000);
    return () => clearInterval(i);
  }, [phone]);

  const downloadDoc = async (m) => {
    try {
      const path = (m.file_url || "").replace(/^\/api/, "");
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const send = async () => {
    if (!text.trim() || busy) return;
    const outbound = text;
    push({ who: "me", text: outbound });
    setText(""); setBusy(true);
    try {
      const { data } = await api.post("/messages/simulate", { from_phone: phone, text: outbound, message_type: "text" });
      push({ who: "isa", text: data.reply, kind: data.kind, sources: data.sources });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    push({ who: "me", text: file.name });
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("from_phone", phone); fd.append("text", text || "");
    setText("");
    try {
      const { data } = await api.post("/messages/simulate-upload", fd);
      push({ who: "isa", text: data.reply, kind: data.kind });
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="mx-auto flex h-[calc(100vh-9rem)] max-w-2xl flex-col">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">Simulador WhatsApp</h1>
        <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-1.5">
          <Phone size={14} className="text-zinc-400" />
          <select data-testid="phone-select" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-transparent text-sm text-zinc-700 focus:outline-none">
            {emps.map((e) => <option key={e.id} value={e.phone}>{`${e.name} (${e.role})`}</option>)}
            <option value="5500000000000">Número não autorizado</option>
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto rounded-xl border border-zinc-200 bg-zinc-50 p-4" data-testid="chat-window">
        {!msgs.length && (
          <div className="flex h-full flex-col items-center justify-center text-center text-zinc-400">
            <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600"><Sparkles size={24} /></span>
            <p className="max-w-sm text-sm">Fale como no WhatsApp: "Sai do TJ Tocantins, Dr. Souza adorou a proposta de R$ 380 mil"; depois pergunte "Como tá o TJ Tocantins?".</p>
          </div>
        )}
        <div className="space-y-3">
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.who === "me" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.who === "me" ? "rounded-br-sm bg-zinc-900 text-white" : "rounded-bl-sm border border-zinc-200 bg-white text-zinc-800"}`}>
                {m.who === "isa" && m.kind && <div className="mb-1"><Badge tone={KIND_TONE[m.kind] || "muted"}>{m.kind}</Badge></div>}
                <p className="whitespace-pre-wrap">{m.text || (m.kind === "rejected" ? "(número não autorizado — mensagem ignorada em silêncio)" : "")}</p>
                {m.media_type === "document" && m.file_url && (
                  <button data-testid="inbox-doc-download" onClick={() => downloadDoc(m)}
                    className="mt-2 flex items-center gap-1.5 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-[11px] hover:text-indigo-600">
                    <Paperclip size={12} /> Abrir documento
                  </button>
                )}
                {m.sources?.length > 0 && (
                  <p className="mt-2 border-t border-zinc-200 pt-1 text-[11px] text-zinc-400">
                    {m.sources.length} fonte(s) · origem rastreável
                  </p>
                )}
              </div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="rounded-2xl rounded-bl-sm border border-zinc-200 bg-white px-4 py-2.5 text-sm text-zinc-400">Isa está pensando...</div></div>}
          <div ref={endRef} />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button onClick={() => fileRef.current?.click()} data-testid="attach-btn" className="rounded-md border border-zinc-200 bg-white p-2.5 text-zinc-400 hover:text-zinc-900">
          <Paperclip size={18} />
        </button>
        <input ref={fileRef} type="file" className="hidden" onChange={upload} accept=".pdf,image/*,audio/*" />
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Digite como se fosse o WhatsApp..." className="flex-1 rounded-md border border-input bg-white px-4 py-2.5 text-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/15" />
        <button onClick={send} disabled={busy} data-testid="send-btn" className="rounded-md bg-zinc-900 p-2.5 text-white transition active:scale-95 hover:bg-zinc-800 disabled:opacity-50">
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}
